import { Alert, DateFormat, Flex, Group, Icon, Legend, Tag, Text } from "components/common"
import StructuredList from "components/common/structuredList/structuredList"
import { Row } from "antd"
import { Link } from "react-router-dom"
import dayjs from "dayjs"
import { t } from "i18next"
import theme from "utils/themeVars"
import plusCodeImage from "../images/plusCodeCopy.png"
import { isSubTaskMeetingRescheduled } from "features/tsr/components/tsrDrawer/tsrComponent/utilis"
import { IconText, Separator } from "components/custom"
import { joinBytItems, localisedValue } from "utils"
import getPrivilege from "utils/privileges/privileges"

const PLUS_CODE_INSTRUCTIONS = () => [
  t("Find the Plus Code by tapping the location on Google Maps."),
  t("Look for the code, like G9H4+Q6, usually below the address, as shown in the image."),
  t("On web, tap the copy icon; on mobile, simply tap the Plus Code to copy."),
]

const SALE_EVENT_KEYS = {
  sale_event: true,
  sale_event_meeting_arranged: true,
  sale_event_meeting_pushed: true,
  sale_event_meeting_attempted: true,
}

const TSR_SALE_EVENT_KEYS = {
  sale_event_meeting_pushed: true,
  sale_event_meeting_rescheduled: true,
}

const MEETING_KEYS = {
  walk_in: true,
  meeting_done: true,
  meeting_attempted: true,
  meeting_rescheduled: true,
}

const OFFICE_KEYS = {
  company_office: true,
  company_office_meeting_attempted: true,
  company_office_meeting_rescheduled: true,
}

const SITE_OFFICE_KEYS = {
  site_office: true,
  site_office_meeting_attempted: true,
  site_office_meeting_rescheduled: true,
}

const PROPOSAL_KEYS = {
  proposal_message: true,
}

const SUB_OF_SUB_REQUIRED = {
  meeting_done: true,
  meeting_attempted: true,
  walk_in: true,
  meeting_rescheduled: true,
  video_meeting: true,
}

const RENDER_DATETIME_KEYS = {
  meeting_done: true,
  video_meeting: true,
  walk_in: true,
  meeting_attempted: true,
}

const ATTACHMENT_REQUIRED_KEYS = {
  video_meeting: true,
  walk_in: true,
  meeting_done: true,
}

const MEETING_ARRANGED_PUSHED = {
  meeting_arranged: true,
  meeting_pushed: true,
}

const PDF_ALLOWED_KEYS = { im_proposal: true, investment_brief: true, is_proposal: true, proposal_message: true }

const getOptionKey = option => option?.children?.[0]?.key || option?.key || option

export const isWalkInOrMeeting = option => !!MEETING_KEYS[getOptionKey(option)]
export const isCompanyOffice = option => !!OFFICE_KEYS[getOptionKey(option)]
export const isSiteOffice = option => !!SITE_OFFICE_KEYS[getOptionKey(option)]
export const isProposalOrVideoMeetingOrWalkIn = (subTask, subOfSubTasks) =>
  !!PROPOSAL_KEYS[subTask] || (subTask === "im_proposal" && subOfSubTasks === "im_proposal_shared")
export const isRequiredSubofSubTask = key => !!SUB_OF_SUB_REQUIRED[key]
export const isRenderMeetingDateTime = key => !!RENDER_DATETIME_KEYS[key]
export const isAttachmentVerificationRequired = key => !!ATTACHMENT_REQUIRED_KEYS[key]

export const isMeetingAttachmentMandatory = (subTask, canAddMeetingWithoutAttachment = false) =>
  !!ATTACHMENT_REQUIRED_KEYS[subTask] && !canAddMeetingWithoutAttachment

export const isVideoMeetingAttachmentOptional = (subTask, isBayutPlatform) =>
  subTask === "video_meeting" && isBayutPlatform

export const isMeetingArrangedOrPushed = key => !!MEETING_ARRANGED_PUSHED[key]
export const isPDFAllowed = key => !!PDF_ALLOWED_KEYS[key]

export const isSubTaskSalesEvent = (taskSubChildren = [], subOfSubTasks, isTelesales = false) => {
  const SALE_KEYS = isTelesales ? TSR_SALE_EVENT_KEYS : SALE_EVENT_KEYS
  return taskSubChildren?.some(({ key }) => key === subOfSubTasks && !!SALE_KEYS[key])
}

export const isBayutGoogleMeetArranged = (subTask, subOfSubTask, subLocation, isBayutPlatform) => {
  return (
    isBayutPlatform &&
    subTask === "meeting_arranged" &&
    subOfSubTask === "video_meeting_meeting_arranged" &&
    subLocation === "google_meet_meeting_arranged"
  )
}

export const isSaleEventAndMeetingDone = (value, saleEvent) => {
  return (
    saleEvent &&
    value === "meeting_done" &&
    dayjs().isSameOrBefore(dayjs(saleEvent?.SalesEvent?.endTimeStamp)) &&
    dayjs().isSameOrAfter(dayjs(saleEvent?.SalesEvent?.startTimeStamp))
  )
}

export const filterSubTaskchildren = (subchildren = [], salesEvents = {}, isTelesales = false) => {
  if (!Array.isArray(subchildren)) return []
  const SALE_KEYS = isTelesales ? TSR_SALE_EVENT_KEYS : SALE_EVENT_KEYS
  const hasSaleEvent = subchildren?.some(({ key }) => SALE_KEYS[key])
  const noSalesRecorded = !salesEvents?.saleEvents?.length
  return hasSaleEvent && noSalesRecorded ? subchildren.filter(({ key }) => !SALE_KEYS[key]) : subchildren
}

export const getPlusCodeTooltip = () => (
  <>
    <Legend title={t("How to Get Plus Code")} />
    <StructuredList
      items={PLUS_CODE_INSTRUCTIONS()}
      listTagProps={{ style: { paddingLeft: 18, marginBottom: 12, color: theme.gray900 } }}
    />
    <img src={plusCodeImage} alt="Plus Code Example" style={{ borderRadius: 4, marginBottom: 12 }} />
    <Text>{t("For more information,")}</Text>{" "}
    <Link
      target="_blank"
      to="https://support.google.com/maps/answer/7047426?hl=en"
      className="pointer"
      style={{ color: theme["primary-color"] }}
    >
      {t("visit Google Maps Help")}
    </Link>
  </>
)

export const renderSuggestedComments = ({ suggestedComments = [], setFieldsValue = () => {} }) => {
  if (!suggestedComments.length) return null

  return (
    <Group gap="8px" className="p-12" template="auto 1fr">
      <Text type="secondaryLight">{t("Suggested Comments")}:</Text>
      <Row gutter={[8, 8]}>
        {suggestedComments.map(comment => (
          <Tag
            key={comment}
            type="light"
            color="#A7A7A7"
            bordered
            pill
            onClick={() => setFieldsValue({ comment })}
            maxLength={comment.length > 50 ? 29 : undefined}
            tooltipText={comment.length > 50 && comment}
          >
            <Icon icon="HiPlus" size={12} style={{ marginInlineEnd: 4 }} />
            {comment}
          </Tag>
        ))}
      </Row>
    </Group>
  )
}

export const getSubTasks = (taskTypes, task, leadDetailInfo, isEditable = false) => {
  const selectedTask = taskTypes?.subTasksMap?.[task]
  const options = selectedTask?.data?.map(e =>
    e?.key === "meeting_pushed" && leadDetailInfo?.isTaskInQCState && !isEditable ? { ...e, disabled: true } : e
  )
  return { options, subTasksMap: selectedTask?.subOfSubTasks }
}

export const buildTaskTypeParams = ({ userInfo, lead, isEditable, leadDetailInfo, userPrivileges }) => {
  const { teamType, AgencyConfiguration } = userInfo || {}
  const platformKey = AgencyConfiguration?.Platform?.platformKey
  const isSalesTeam = ["sales", "secondarySales", "support"].includes(teamType)
  const isZameenPlatform = platformKey === "zameen"
  const stage = leadDetailInfo?.inquiry?.MeetingPushedInstances?.[0]?.CurrentStage?.key
  const saleEventStatus = !!leadDetailInfo?.inquiry.MeetingPushedInstances?.[0]?.InitiatorTask?.salesEventId
  const haveMeetingDonePrivilege = getPrivilege("can_add_meeting_done_task_web", userPrivileges)
  return {
    withNextStatuses: 1,
    leadType: lead?.interestedIn,
    haveMeetingDonePrivilege,
    ...(isEditable && { from: "edit_task" }),
    ...(isZameenPlatform && isSalesTeam && { teamType }),
    ...(leadDetailInfo?.inquiry?.MeetingPushedInstances?.[0] && { stage, saleEventStatus }),
  }
}

export function getDeadlineRules({
  now = dayjs(),
  taskTypes,
  leadDetailInfo,
  setFieldsValue,
  nextTask,
  subTask,
  selectedTimeSlot,
  showDateSlot,
}) {
  const todoLogic = taskTypes?.todoLogic?.[subTask]

  const meetingTime = isSubTaskMeetingRescheduled(subTask)
    ? selectedTimeSlot?.[0]
    : leadDetailInfo?.inquiry?.MeetingPushedInstances?.[0]?.InitiatorTask?.CalendarEvent?.startTime

  const meetingDateTime = !!meetingTime && dayjs(meetingTime)
  const lastDeadline = dayjs(leadDetailInfo?.tasks?.[0]?.todoDetails?.nextStatusDeadline)

  const isDateSlotVisible = (subTask === "meeting_pushed" || nextTask === "meeting_done") && showDateSlot

  if (!meetingTime || !meetingDateTime) return {}
  switch (todoLogic?.logicType) {
    case "MATCH_MEETING_TIME":
      if (isDateSlotVisible) {
        setFieldsValue?.({ dateSlot: meetingDateTime, timeSlot: [meetingDateTime, meetingDateTime.add(1, "hour")] })
      } else {
        setFieldsValue?.({ deadLine: meetingDateTime })
      }
      return {
        disabledDeadline: todoLogic?.isDeadlineEnforced,
      }

    case "CONDITIONAL_OFFSET_BEFORE_MEETING":
      const date = meetingDateTime.subtract(meetingDateTime.isSame(now, "day") ? 1 : 2, "hour")
      if (isDateSlotVisible) {
        setFieldsValue?.({ dateSlot: date, timeSlot: [date, date.add(1, "hour")] })
      } else {
        setFieldsValue?.({ deadLine: date })
      }
      return {
        disabledDeadline: todoLogic?.isDeadlineEnforced,
        endDate: meetingDateTime,
      }

    case "ANY_TIME_BEFORE_MEETING":
      if (isDateSlotVisible) {
        setFieldsValue?.({ dateSlot: meetingDateTime, timeSlot: [meetingDateTime, meetingDateTime.add(1, "hour")] })
      } else {
        setFieldsValue?.({ deadLine: meetingDateTime })
      }
      return {
        endDate: meetingDateTime,
        disabledDeadline: todoLogic?.isDeadlineEnforced,
      }

    case "NO_DEADLINE":
      return {
        disabledDeadline: todoLogic?.isDeadlineEnforced,
      }

    case "PRE_MEETING_CONFIRMATION":
      const selectedDate =
        nextTask === "meeting_confirmed" && lastDeadline?.isAfter(now) ? lastDeadline : meetingDateTime
      if (isDateSlotVisible) {
        setFieldsValue?.({ dateSlot: selectedDate, timeSlot: [selectedDate, selectedDate.add(1, "hour")] })
      } else {
        setFieldsValue?.({ deadLine: selectedDate })
      }
      return {
        endDate: meetingDateTime,
      }

    case "MATCH_FUTURE_MEETING_TIME":
      if (meetingDateTime.isAfter(now)) {
        if (isDateSlotVisible) {
          setFieldsValue?.({ dateSlot: meetingDateTime, timeSlot: [meetingDateTime, meetingDateTime.add(1, "hour")] })
        } else {
          setFieldsValue?.({ deadLine: meetingDateTime })
        }
        return {
          disabledDeadline: todoLogic?.isDeadlineEnforced,
        }
      } else {
        return {
          disabledDeadline: todoLogic?.isDeadlineEnforced,
        }
      }
    default:
      return {
        meetingPushedDeadline: null,
        disabledDeadline: false,
      }
  }
}

export function alertMessageDetails(leadDetailInfo) {
  const meetingTime =
    leadDetailInfo?.inquiry?.MeetingPushedInstances?.[0]?.InitiatorTask?.CalendarEvent?.startTime || dayjs()

  const location = localisedValue({ object: leadDetailInfo?.mostRecentLocationTask, key: "name" })
  let taskState = "none"
  if (leadDetailInfo?.isTaskInQCState) taskState = "QC"
  else if (leadDetailInfo?.isTaskInPendingConfirmation) taskState = "PENDING"
  else if (leadDetailInfo?.isTaskConfirmed) taskState = "CONFIRMED"

  switch (taskState) {
    case "QC":
      return {
        show: false,
        text: t("A meeting is pushed for this client which is under QC evaluation."),
        meetingTime,
        location,
      }

    case "PENDING":
      return {
        show: true,
        text: t("A meeting is pending confirmation on this client"),
        meetingTime,
        location,
      }

    case "CONFIRMED":
      return {
        show: true,
        text: t("A meeting is confirmed on this client"),
        meetingTime,
        location,
      }

    default:
      return { noAlert: true }
  }
}

export function getAlert(leadDetailInfo) {
  const qc = alertMessageDetails(leadDetailInfo)
  if (qc?.noAlert) return
  return (
    <Alert
      style={{
        backgroundColor: theme["gray200"],
        borderColor: theme["gray300"],
      }}
      message={
        <Flex justify="space-between" style={{ color: theme["gray800"], width: "100%" }}>
          <Text color={theme["gray800"]}>{qc?.text}</Text>

          {qc?.show && (
            <span
              style={{
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {joinBytItems(
                [
                  <IconText
                    icon="HiLocationMarker"
                    iconProps={{ color: theme["gray800"], size: "1em" }}
                    textType="secondary"
                    title={qc?.location}
                  />,
                  <DateFormat date={qc?.meetingTime} showTime={true} color={theme["gray800"]} />,
                ],
                <Separator />
              )}
            </span>
          )}
        </Flex>
      }
    />
  )
}
